import { appConfig } from "@/constants";
import { earnGem } from "@/domain/gems-game/data-access";
import type { WalletGems } from "@/domain/gems-game/data-access/lib/wallet_gems";
import { useConnection } from "@/hooks/connection";
import { notifyError } from "@/utils/monitoring";
import { trackLegacyEvent } from "@analytics";
import { Button, Stack } from "@biom3/react";
import { useCallback, useEffect, useState } from "react";
import { EOAWalletDisconnect } from "./EOAWalletDisconnect";

const TIME_TIL_SPINNING_STOPS = 3000;

interface GetYourGemProps {
  onGemEarned: (walletGems: WalletGems) => void;
  walletGems: WalletGems | undefined;
  hasJustEarned: boolean;
  disconnect: () => Promise<void>;
  isPassportWallet: boolean;
}

// Button states
const IsClaimingButton = () => (
  <Button
    size="large"
    testId="claim-gems__claiming-btn"
    disabled
    sx={{ minWidth: ["auto", "260px"] }}
  >
    Claiming...
  </Button>
);

const RevealGemsButton = () => (
  <Button
    size="large"
    testId="claim-gems__reveal-btn"
    disabled
    sx={{ minWidth: ["auto", "260px"] }}
  >
    Revealing Gems...
  </Button>
);

const ClaimedButton = () => (
  <Button
    size="large"
    testId="claim-gems__claimed-btn"
    disabled
    sx={{ minWidth: ["auto", "260px"] }}
  >
    Gems Claimed!
    <Button.Icon icon="ImxRewards" />
  </Button>
);

export function GemsClaimCta({
  onGemEarned,
  walletGems,
  hasJustEarned,
  disconnect,
  isPassportWallet,
}: GetYourGemProps) {
  const { provider, walletAddress } = useConnection();
  const [isClaiming, setIsClaiming] = useState(false);
  const [isGettingReady, setIsGettingReady] = useState(false);

  useEffect(() => {
    if (hasJustEarned) {
      setIsGettingReady(true);
      const timer = setTimeout(() => {
        setIsGettingReady(false);
      }, TIME_TIL_SPINNING_STOPS);
      return () => clearTimeout(timer);
    }
  }, [hasJustEarned]);

  const getYourGem = useCallback(async () => {
    if (!provider) throw new Error("Provider is not connected");
    if (!walletAddress) throw new Error("Wallet address is not connected");
    try {
      setIsClaiming(true);

      trackLegacyEvent({
        screen: "Claim",
        action: "Pressed",
        control: "GetYourGem",
        controlType: "Button",
        userJourney: "Claim",
        extras: {
          variableGemsEnabled: true,
          walletAddress,
        },
      });

      await earnGem(
        provider,
        walletAddress,
        appConfig.GEMS_GAME_CONTRACT_ADDRESS,
      );

      if (walletGems) {
        onGemEarned(walletGems);
      }
      setIsClaiming(false);
    } catch (e: unknown) {
      setIsClaiming(false);
      const EOA_CONNECTION_REJECTED_CODE = 4001;
      const PASSPORT_TRANSACTION_REJECTED = -32003;
      if (
        e instanceof Error &&
        (!("code" in e) ||
          (e.code !== EOA_CONNECTION_REJECTED_CODE &&
            e.code !== PASSPORT_TRANSACTION_REJECTED))
      ) {
        notifyError(e, "getYourGem");
      }
    }
  }, [onGemEarned, provider, walletAddress, walletGems]);

  if (isClaiming) {
    return <IsClaimingButton />;
  }

  if (isGettingReady) {
    return <RevealGemsButton />;
  }

  if (hasJustEarned) {
    return <ClaimedButton />;
  }

  return (
    <Stack direction={["column"]}>
      {!isPassportWallet && walletAddress ? (
        <EOAWalletDisconnect
          walletAddress={walletAddress}
          disconnect={disconnect}
        />
      ) : null}
      <Button
        size="large"
        testId="claim-gems__claim-btn"
        onClick={getYourGem}
        sx={{ minWidth: ["auto", "260px"] }}
      >
        Claim Gems
        <Button.Icon icon="ImxRewards" />
      </Button>
    </Stack>
  );
}
